import {
  Dialog,
  DialogBackdrop,
  DialogPanel,
  Transition,
  TransitionChild,
} from "@headlessui/react";
import {
  Fragment,
  HTMLAttributes,
  MutableRefObject,
  PropsWithChildren,
  forwardRef,
  useEffect,
  useState,
} from "react";

import {
  ModalThemeProps,
  modalTheme,
} from "hardcover-ui/theme/components/modal";
import classNames from "lib/classNames";

type Props = {
  variant?: ModalThemeProps["variant"];
  size?: ModalThemeProps["size"];
  className?: string;
  open?: boolean;
  onClose: () => void;
  initialFocus?: MutableRefObject<HTMLElement | null> | undefined;
  position?: "center" | "top" | "bottom";
  zIndex?: number;
  disablePopState?: boolean;
  modalTitle?: string;
} & Partial<HTMLAttributes<HTMLDivElement>>;

// Close the modal if navigating to a different page
function ModalController({ onClose }: { onClose: () => void }) {
  const [initialPath] = useState(window.location.pathname);
  const [initialSearch] = useState(window.location.search);

  // Close the modal if navigating to a different page
  useEffect(() => {
    const handlePopState = () => {
      if (
        onClose &&
        (window.location.pathname !== initialPath ||
          window.location.search !== initialSearch)
      ) {
        onClose();
      }
    };

    window.addEventListener("popstate", handlePopState);

    return () => {
      window.removeEventListener("popstate", handlePopState);
    };
  }, [initialPath, initialSearch, onClose]);
  return <></>;
}

const Modal = forwardRef<HTMLDivElement, PropsWithChildren<Props>>(
  (
    {
      children,
      initialFocus,
      variant = "primary",
      size = "sm",
      open = false,
      onClose,
      position = "center",
      zIndex = 40,
      className,
      disablePopState,
      modalTitle = "Modal",
      ...rest
    },
    ref,
  ) => {
    const finalClassName = classNames(
      modalTheme.variant[variant],
      modalTheme.size?.[size],
      className,
    );

    const props = {
      ref,
      className: finalClassName,
      ...rest,
    };

    return (
      <Transition show={open} as={Fragment}>
        <Dialog
          as="div"
          title={modalTitle}
          static
          className={"modal fixed inset-0 overflow-y-auto"}
          open={open}
          onClose={() => {
            if (!disablePopState) {
              onClose();
            }
          }}
          initialFocus={initialFocus}
          style={{ zIndex }}
        >
          <div className="flex justify-center text-center max-h-screen">
            <TransitionChild
              as={Fragment}
              enter="ease-out duration-400"
              enterFrom="opacity-0"
              enterTo="opacity-100"
              leave="ease-in duration-100"
              leaveFrom="opacity-100"
              leaveTo="opacity-0"
            >
              <DialogBackdrop className="fixed inset-0 bg-[rgba(255,255,255,0.7)] dark:bg-[rgba(17,24,39,0.7)] transition-all" />
            </TransitionChild>

            <div className="fixed inset-0 overflow-y-auto">
              <div
                style={{
                  paddingTop: "var(--safe-area-inset-top)",
                }}
                className={classNames(
                  "bg-background md:bg-transparent dark:md:bg-transparent",
                  "flex min-h-full lg:p-4 text-center",
                  position === "center" ? "items-center justify-center" : "",
                  position === "top" ? "items-start justify-start" : "",
                )}
              >
                {!disablePopState && onClose && (
                  <ModalController onClose={onClose} />
                )}
                <TransitionChild
                  as={Fragment}
                  enter="ease-out duration-200"
                  enterFrom="opacity-0 scale-100"
                  enterTo="opacity-100 scale-100"
                  leave="ease-in duration-100"
                  leaveFrom="opacity-100 scale-100"
                  leaveTo="opacity-0 scale-100"
                >
                  <DialogPanel {...props}>{children}</DialogPanel>
                </TransitionChild>
              </div>
            </div>
          </div>
        </Dialog>
      </Transition>
    );
  },
);

export default Modal;
